<script setup>
import { computed } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { formatDayMonthDate, formatShortDateTime } from '@/lib/utils'
import { formatDuration } from '@/composables/report'
import { useDetectDevice } from '@/composables/device'
import { useNavigator } from '@/composables/navigator'
import { infoBoxToneEnum } from '@/stores/portal.store'

import InfoBox from '@/components/shared/InfoBox.vue'
import ListItem from '@/components/shared/ListItem.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'

const props = defineProps({
  changes: {
    type: Object,
    required: true,
  },
  dataAsOf: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['recompute', 'cancel'])

const i18n = getI18n()
const { isMobile } = useDetectDevice()
useNavigator(() => emit('cancel'))

const groups = computed(() => [
  { key: 'added', label: i18n.t('common.report.pendingAddedGroup'), icon: 'mdi-plus', rows: props.changes.rows.filter(change => change.is_new) },
  { key: 'changed', label: i18n.t('common.report.pendingChangedGroup'), icon: 'mdi-pencil', rows: props.changes.rows.filter(change => !change.is_new) },
].filter(group => group.rows.length))
const deletedCount = computed(() => props.changes.deleted_count)

const caption = (change) => i18n.t(change.is_new ? 'common.report.addedOn' : 'common.report.changedOn', { date: formatShortDateTime(change.is_new ? change.created_at : change.updated_at) })
</script>

<template>
  <q-card class="dialog-card-flex" :style="isMobile ? undefined : 'max-height: 85vh;'">
    <navigate-back :text="i18n.t('common.report.pendingTitle')" @cancel="emit('cancel')" />
    <q-card-section class="scroll col q-pa-sm">
      <info-box :tone="infoBoxToneEnum.WARNING" :message="i18n.t('common.report.pendingIntro', { date: props.dataAsOf ? formatShortDateTime(props.dataAsOf) : '—' })" />
      <q-list v-for="group in groups" :key="group.key">
        <q-item-label header class="text-primary text-weight-medium q-pb-xs">{{ group.label }} ({{ group.rows.length }})</q-item-label>
        <list-item
          v-for="change in group.rows"
          :key="change.id"
          class="q-px-sm"
          :avatar-icon="group.icon"
          avatar-icon-color="primary"
          :label="[formatDayMonthDate(change.date), change.staff_label, change.project_name].filter(Boolean).join(' · ')"
          :caption="caption(change)"
          :right-label="change.work_minutes ? formatDuration(change.work_minutes) : null"
          dense
        />
      </q-list>
      <q-list v-if="deletedCount">
        <q-item-label header class="text-primary text-weight-medium q-pb-xs">{{ i18n.t('common.report.pendingDeletedGroup') }} ({{ deletedCount }})</q-item-label>
        <list-item
          class="q-px-sm"
          avatar-icon="mdi-minus"
          avatar-icon-color="negative"
          :label="i18n.t('common.report.pendingDeleted', { count: deletedCount })"
          dense
        />
      </q-list>
    </q-card-section>
    <q-card-actions align="center" class="dialog-card-bottom-bar">
      <q-btn
        class="large-btn"
        color="primary"
        icon="mdi-refresh"
        :label="i18n.t('common.report.recompute')"
        @click="emit('recompute')"
      />
    </q-card-actions>
  </q-card>
</template>
